/**
 * The consent gate — the hard legal gate before any play.
 *
 * Rules this screen enforces:
 *  - both documents are *shown here*, not just linked (with the full pages one click away);
 *  - the agree control stays disabled until the player has reached the end of both documents and
 *    ticked the explicit acknowledgement;
 *  - what is sent is the exact versions the player was shown — a version bump mid-session is
 *    rejected by the server and the gate re-renders;
 *  - declining signs the player out. There is no third path.
 */
import { h, query } from '../dom.js';
import { t } from '../../i18n/index.js';
import { api, ApiError } from '../../api/client.js';
import type { LegalDocumentPayload } from '../../api/types.js';
import { banner, footer } from '../components/chrome.js';
import type { View } from '../view.js';

export interface ConsentContext {
  documents: LegalDocumentPayload[];
  onAccepted: () => void;
  onDeclined: () => void;
}

export function consentView({ documents, onAccepted, onDeclined }: ConsentContext): View {
  const read = new Set<string>();
  const panes: HTMLElement[] = [];
  const tabs: HTMLButtonElement[] = [];

  const checkbox = h('input', {
    type: 'checkbox',
    id: 'nb-consent-agree',
    class: 'nb-checkbox__input',
  }) as HTMLInputElement;

  const agreeButton = h('button', {
    class: 'nb-button nb-button--alert',
    type: 'button',
    disabled: true,
    text: t('consent.agree'),
  }) as HTMLButtonElement;

  const hint = h('p', { class: 'nb-hint', text: t('consent.scrollHint') });
  const errorSlot = h('div', { class: 'nb-slot' });

  const refreshGate = (): void => {
    const allRead = documents.every((doc) => read.has(doc.id));
    agreeButton.disabled = !(allRead && checkbox.checked);
    hint.hidden = allRead;
  };

  checkbox.addEventListener('change', refreshGate);

  const selectTab = (index: number): void => {
    tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    panes.forEach((pane, i) => {
      pane.hidden = i !== index;
    });
  };

  documents.forEach((doc, index) => {
    const tab = h('button', {
      class: 'nb-tab',
      type: 'button',
      role: 'tab',
      id: `nb-tab-${doc.id}`,
      'aria-controls': `nb-pane-${doc.id}`,
      'aria-selected': String(index === 0),
      text: t(doc.id === 'eula' ? 'consent.tab.eula' : 'consent.tab.privacy'),
      onclick: () => selectTab(index),
    }) as HTMLButtonElement;
    tabs.push(tab);

    const body = h('div', { class: 'nb-legal-body', html: doc.html });
    const scroller = h('div', { class: 'nb-legal-scroll', tabindex: '0', role: 'document' }, body);

    // "Read" = scrolled to the bottom of that document. A short document that needs no scrolling
    // counts as read immediately (checked after mount, when heights are known).
    const markReadIfAtEnd = (): void => {
      const atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 24;
      if (atEnd) {
        read.add(doc.id);
        refreshGate();
      }
    };
    scroller.addEventListener('scroll', markReadIfAtEnd, { passive: true });

    const pane = h(
      'section',
      {
        class: 'nb-pane',
        id: `nb-pane-${doc.id}`,
        role: 'tabpanel',
        'aria-labelledby': `nb-tab-${doc.id}`,
        hidden: index !== 0,
      },
      h(
        'header',
        { class: 'nb-pane__head' },
        h('h2', { class: 'nb-pane__title', text: doc.title }),
        h('p', {
          class: 'nb-meta',
          text: t('consent.version', { version: doc.version, date: doc.effectiveDate }),
        }),
        h('a', {
          class: 'nb-link-small',
          href: doc.path,
          target: '_blank',
          rel: 'noopener',
          text: t('consent.openFull'),
        }),
      ),
      scroller,
    );
    panes.push(pane);

    // Run once after layout so a pane that fits without scrolling is already "read".
    requestAnimationFrame(markReadIfAtEnd);
  });

  agreeButton.addEventListener('click', () => {
    const versions = {
      eula: documents.find((doc) => doc.id === 'eula')?.version ?? '',
      privacy: documents.find((doc) => doc.id === 'privacy')?.version ?? '',
    };
    agreeButton.disabled = true;
    agreeButton.textContent = t('consent.pending');
    errorSlot.replaceChildren();

    void api
      .acceptConsent(versions)
      .then((status) => {
        if (status.satisfied) onAccepted();
        else throw new ApiError(409, 'version_changed', status);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof ApiError && err.code === 'version_changed'
            ? t('consent.versionChanged')
            : t('consent.error');
        errorSlot.replaceChildren(banner('alert', message));
        agreeButton.textContent = t('consent.agree');
        refreshGate();
      });
  });

  const element = h(
    'div',
    { class: 'nb-page nb-page--consent' },
    h(
      'main',
      { class: 'nb-consent', id: 'main' },
      h(
        'header',
        { class: 'nb-consent__head' },
        h('h1', { class: 'nb-consent__title', text: t('consent.title') }),
        h('p', { class: 'nb-lede', text: t('consent.lede') }),
      ),
      h('div', { class: 'nb-tabs', role: 'tablist', 'aria-label': t('consent.title') }, ...tabs),
      ...panes,
      errorSlot,
      h(
        'div',
        { class: 'nb-consent__gate' },
        h(
          'label',
          { class: 'nb-checkbox', for: 'nb-consent-agree' },
          checkbox,
          h('span', { text: t('consent.checkbox') }),
        ),
        hint,
        h(
          'div',
          { class: 'nb-consent__actions' },
          agreeButton,
          h('button', {
            class: 'nb-button nb-button--quiet',
            type: 'button',
            text: t('consent.decline'),
            onclick: () => onDeclined(),
          }),
        ),
      ),
    ),
    footer(),
  );

  // Keyboard support for the tablist (left/right between documents).
  const tablist = query<HTMLElement>(element, '.nb-tabs');
  tablist?.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = (current + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
    selectTab(next);
    tabs[next]?.focus();
    event.preventDefault();
  });

  return { element };
}
